import type { MowerId } from '../../content/mowers';
import { Dir, type Direction } from '../../sim/direction';

/**
 * Where each mower's effects come from, read off its art (mower3q.ts) for each facing: the
 * clippings' way out (a rotary mower's discharge chute or deck side, a reel's blades, the
 * self-propelled mower's bag) and which way they are thrown, and the exhaust. Offsets are art
 * pixels from the center of the mower's frame (its tile's center); west mirrors east.
 */
export interface MowerFxPoint {
  x: number;
  y: number;
}

export interface MowerFx {
  /** Where clippings leave the mower. */
  discharge: MowerFxPoint;
  /** The way they are thrown (degrees: 0 east, 90 south). */
  aim: number;
  /** Thrown hard out of a chute or off a reel, or puffing gently out of a grass bag. */
  kind: 'throw' | 'bag';
  /** Where exhaust rises from (null: no engine). */
  exhaust: MowerFxPoint | null;
}

type Facing = 'side' | 'front' | 'back';
interface FacingFx {
  d: [number, number];
  aim: number;
  ex: [number, number] | null;
}

const FX: Record<MowerId, { kind: MowerFx['kind']; at: Record<Facing, FacingFx> }> = {
  // Rotary decks discharge out of their right-hand side (the side nearest us heading east).
  push: {
    kind: 'throw',
    at: {
      side: { d: [2, 5], aim: 100, ex: [0, -3] },
      front: { d: [-8, 1], aim: 170, ex: [0, -3] },
      back: { d: [8, -1], aim: 10, ex: [0, -4] },
    },
  },
  // Clippings blow back into the bag; a few puff out of it.
  selfPropelled: {
    kind: 'bag',
    at: {
      side: { d: [-10, -1], aim: 200, ex: [0, -3] },
      front: { d: [0, -9], aim: 270, ex: [0, -3] },
      back: { d: [0, 6], aim: 90, ex: [0, -4] },
    },
  },
  // The reel flings them forward off its blades; no engine.
  reel: {
    kind: 'throw',
    at: {
      side: { d: [6, 1], aim: 350, ex: null },
      front: { d: [0, 5], aim: 90, ex: null },
      back: { d: [0, -6], aim: 270, ex: null },
    },
  },
  // Out of the chute under its right side; the engine is under the hood, up front.
  tractor: {
    kind: 'throw',
    at: {
      side: { d: [1, 7], aim: 95, ex: [6, -3] },
      front: { d: [-8, 0], aim: 175, ex: [4, 4] },
      back: { d: [8, -3], aim: 5, ex: [4, -9] },
    },
  },
  // Out of the deck's chute on its right; the engine sits behind the seat.
  zeroTurn: {
    kind: 'throw',
    at: {
      side: { d: [3, 7], aim: 95, ex: [-11, -6] },
      front: { d: [-11, 2], aim: 175, ex: [3, -12] },
      back: { d: [10, -7], aim: 5, ex: [3, 4] },
    },
  },
  // Each reel of the gang throws them forward (its middle one here; the view adds the wings).
  fairway: {
    kind: 'throw',
    at: {
      side: { d: [15, 0], aim: 0, ex: [-14, -6] },
      front: { d: [0, 15], aim: 90, ex: [0, -14] },
      back: { d: [0, -15], aim: 270, ex: [0, 9] },
    },
  },
};

/** The effect points of `id` facing `dir`. */
export function mowerFx(id: MowerId, dir: Direction): MowerFx {
  const spec = FX[id];
  const facing: Facing = dir === Dir.S ? 'front' : dir === Dir.N ? 'back' : 'side';
  const f = spec.at[facing];
  const mirror = dir === Dir.W;
  const point = ([x, y]: [number, number]): MowerFxPoint => ({ x: mirror ? -x : x, y });
  return {
    discharge: point(f.d),
    aim: mirror ? (540 - f.aim) % 360 : f.aim,
    kind: spec.kind,
    exhaust: f.ex ? point(f.ex) : null,
  };
}
